import {AnalyticsContext, type AnalyticsProviderProps} from '@github-ui/analytics-provider/context'
import {type AnalyticsEvent, useClickAnalytics} from '@github-ui/use-analytics'
import React, {useContext, useMemo} from 'react'

/**
 * A context wrapper that provides default values for analytics events in the global navigation.
 * This should be used at the top level of any component tree that is part of the global navigation
 * (e.g. GlobalNavBar, GlobalNavMenu, DashboardLeftRail) to set the `appName` and `category` for all
 * analytics events sent from components within that tree.
 *
 * This will compose with any existing AnalyticsProvider context (including one provided by the app),
 */
export function GlobalNavAnalyticsProvider(
  props: React.PropsWithChildren<Pick<GlobalNavAnalyticsEvent, 'appName' | 'category' | 'subCategory'>>,
) {
  const analyticsContext = useContext(AnalyticsContext)
  const {subCategory, ...overrides} = props

  const mergedContext = useMemo<AnalyticsProviderProps>(() => {
    return {
      ...analyticsContext,
      ...overrides,
      metadata: subCategory ? {...analyticsContext?.metadata, subCategory} : analyticsContext?.metadata,
    } as AnalyticsProviderProps
  }, [analyticsContext, subCategory, overrides])

  return <AnalyticsContext.Provider value={mergedContext}>{props.children}</AnalyticsContext.Provider>
}

/** A hook that provides analytics event tracking for the global navigation. */
export function useGlobalNavAnalytics(subCategory?: string) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const analyticsContext = useContext(AnalyticsContext)

  const category = analyticsContext?.category || 'GLOBAL_NAV'
  const computedSubCategory = subCategory ?? analyticsContext?.metadata?.subCategory

  return useMemo(() => {
    return {
      sendItemClick: (action: string, label?: string) => {
        const eventData: GlobalNavAnalyticsEvent = {
          action,
          category: computedSubCategory ? `${category}:${computedSubCategory}` : category,
          label,
        }

        sendClickAnalyticsEvent(eventData)
      },
    }
  }, [sendClickAnalyticsEvent, category, computedSubCategory])
}

export interface GlobalNavAnalyticsEvent extends Omit<AnalyticsEvent, 'label'> {
  /**
   * The name of the top-level app
   *
   * This should be set by the platform using the AnalyticsProvider and not by individual components,
   * though we will likely override this in our main Rails entry points to maintain consistency as the migration progresses.
   * It will default to the name of the react-partial that the component is in
   * @example `global-nav-bar` | `global-nav-menu` | `dashboard-view`
   */
  appName?: string

  /**
   * The sub-location that the event occurs.
   * This should be set by a context-provider at the top of the component tree that the component is in
   * @example `TopNav` | `UserNav` | `SideBar` | `DashboardLeftRail`
   */
  category?: string

  /**
   * A more specific sub-location within the category.
   * This should be set by the component itself.
   * @example `Teams` | `CreateMenu` | `Favorites` | `UserStatusMenu`
   */
  subCategory?: string

  /**
   * The user action that occurred.
   * This should be set in the click handler of the button/link that was clicked.
   * @example `CLOSE_MENU` | `PROFILE` | `PULL_REQUESTS_HEADER`
   */
  action: string
}

try{ GlobalNavAnalyticsProvider.displayName ||= 'GlobalNavAnalyticsProvider' } catch {}